<style lang="postcss">
	.top-left-box {
		display: flex;
		flex-flow: column nowrap;
		gap: 0.75rem;
		position: fixed;
		top: 1rem;
		left: 1rem;
		z-index: 10;
		max-width: min(34ch, 100%);
	}
	.form-header {
		flex-flow: row nowrap;
		align-items: center;
		gap: 0.35rem;
		top: 0.75rem;
		left: 0.75rem;
		z-index: 10;
		font-size: 0.95rem;
	}
	.form-header-embed {
		display: flex;
		flex-flow: row nowrap;
		align-items: center;
		padding: 2px;
	}
	.form-header-label {
		font-weight: 460;
	}

	.notification-container {
		text-wrap: balance;
		font-family: system-ui, sans-serif, apple-system, "Segoe UI", Roboto, Arial;
		border-radius: 0.2rem;
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 0.25rem;
		font-size: 13px;
		z-index: 10;
	}

	.notification-box {
		padding: 0.375em 0.75rem;
		border-radius: 0.25rem;
		background: #374151;
		color: #f9fafb;
		max-width: 100%;
		overflow-x: auto;
	}
	:global(.notification-box > pre) {
		margin: 4px 0;
		display: block;
		white-space: break-spaces;
		background: rgba(0, 0, 0, 0.2);
		padding: 4px 6px;
		border-radius: 3px;
	}
	.notification-box .notification-link {
		display: inline-block;
		padding: 0.4em 0.25rem;
		color: #a2a7ae;
	}
	.notification-box .notification-link > span {
		font-weight: 500;
		font-size: 0.8rem;
		letter-spacing: 0.2px;
	}

	.notification-box .notification-link:hover {
		color: #d5d7db;
	}

	.notification-box .notification-link.active {
		color: #f9fafb;
	}

	.notification-box.type-error {
		background: #e45454;
	}

	.form-container {
		display: flex;
		flex-direction: column;
		width: 100%;
	}

	.powered-by-link {
		position: fixed;
		border-radius: 0.25rem;
		right: 0.75rem;
		bottom: 0.75rem;
		font-size: 15.5px;
		width: fit-content;
		white-space: nowrap;
		padding: 0.35rem 0.65rem;
		color: var(--theme-color-text);
		background: var(--theme-color);
	}

	@media only screen and (max-width: 768px) {
		.powered-by-link {
			font-size: 15px;
		}
	}
</style>

<script lang="ts">
	import { goto } from "$app/navigation";
	import { page } from "$app/stores";
	import Form from "$form/Form.svelte";
	import { writable } from "svelte/store";
	import type { Writable } from "svelte/store";
	import { getHiddenFields, getImageSrc } from "$lib/helpers/functions";
	import { PUBLIC_APP_URL } from "$env/static/public";
	import { onMount, setContext, getContext } from "svelte";
	import { tooltip } from "design/actions/tooltip";
	import { browser } from "$app/environment";

	export let data;

	setContext("debug", writable([]));

	const debug = getContext<Writable<FormResponseDebug[]>>("debug");
	let integration_errors: string[] = [];

	const iframe = data.iframe ?? false;
	const recordPosthog = data.recordPosthog ?? [];
	const popup = data.popup ?? false;
	const plan = data.plan ?? {};
	const seamless = data.seamless ?? false;
	const test: boolean = Boolean($page.data?.supabase);
	const preloadImages = data.formEmbed.metadata?.preload?.images ?? [];
	const font_urls = data.formEmbed.googleFonts ?? [];
	const translate = data.translate;
	const requestUrl = data.requestUrl;
	const header: FormHeader = data.formEmbed.config.header ?? null;

	function getLink(link: string) {
		return link.startsWith("http") ? link : `https://${link}`;
	}

	function getLanguageLink(language: string) {
		const baseUrl =
			typeof window === "undefined" ? PUBLIC_APP_URL : window.location.href;
		const requestUrlObj = new URL(baseUrl);
		const requestUrlParams = new URL(baseUrl).searchParams;
		for (const [key, value] of requestUrlParams) {
			requestUrlObj.searchParams.set(key, value);
		}
		requestUrlObj.searchParams.set("lang", language);
		return requestUrlObj.toString();
	}

	const powered_by_link =
		import.meta.env.PUBLIC_SITE_URL +
		`?utm_medium=referral&utm_campaign=poweredbylink&utm_content=${data.formEmbed.key}`;

	const hiddenFields = getHiddenFields(
		data.formEmbed.fieldDisplay,
		data.formEmbed.config.logic
	);

	const stripe_field = data.formEmbed.fields.find(
		(field) => field.type === "CreditCard"
	) as CreditCardField;

	const hiddenFieldsCSS =
		hiddenFields.map((field) => `.field[data-field-id="${field}"]`).join(",") +
		"{will-change: transform}";

	let preload = "";
	let update_form = 0;
	const formEmbed =
		data.screenshotHTML || data.bypass
			? {
					...data.formEmbed,
					config: {
						...data.formEmbed.config,
						requireAccessCode: false
					}
				}
			: data.formEmbed;
	$: props = {
		formEmbed: formEmbed,
		formLanguage: data.formLanguage,
		bot: data.bot,
		requestUrl: requestUrl,
		plan: data.plan,
		translate: data.translate,
		countryCode: data.countryCode,
		visitorInfo: data.visitorInfo,
		iframe: data.iframe,
		popup: data.popup,
		seamless: data.seamless,
		test: data.test,
		fromCache: data.fromCache,
		record: data.record,
		cache: data.cache,
		device: data.device,
		disabled: data.disabled,
		disabledType: data.disabledType,
		disabledMessage: data.disabledMessage
	};

	if (preloadImages.length > 0) {
		preloadImages.forEach((image) => {
			preload += `<link rel="preload" as="image" href="${image}">`;
		});
	}

	let coverStyle = "";
	let coverStyleDesktop = "";
	let formStyle = "";
	let formStyleDesktop = "";
	let formCoverStyle = "";
	let formcraftsForm =
		"justify-content: center; margin-top: auto; margin-bottom: auto;";
	if (iframe === true) {
		coverStyle += "padding: 1.25px;";
	}
	if (iframe === false) {
		formStyleDesktop +=
			"padding-top: min(10rem, 10vh); padding-bottom: min(10rem, 10vh);";
		coverStyleDesktop += "justify-content: flex-start;";
	}
	if (data.disabled) {
		formCoverStyle += "margin: auto 3rem;";
	}
	if (
		data.formEmbed.pages.length === 1 &&
		data.formEmbed.styling.welcomeEnabled === false
	) {
		formcraftsForm = "justify-content: flex-start";
	}
	let redirectFn = async (redirect_to: string) => {
		await goto(redirect_to);
		update_form++;
	};
	onMount(async () => {
		if (recordPosthog.includes(data.formEmbed.key) && browser) {
			const posthog = (await import("posthog-js")).default;
			posthog.startSessionRecording();
		}
		if (test) {
			const testFunctions = await import("$lib/helpers/test-functions");
			if (testFunctions) {
				integration_errors = testFunctions.fetchErrors(
					data.formEmbed,
					data.plan
				);
			}
		}
	});
</script>

{#if data.formEmbed.config.enableCustomScript && data.formEmbed.config.customScriptCode}
	{@html data.formEmbed.config.customScriptCode}
{/if}

<svelte:head>
	{#if data.formEmbed.styling.favicon}
		<link
			rel="icon"
			type="image/png"
			href="{getImageSrc(data.formEmbed.styling.favicon)}" />
	{:else}
		<link rel="icon" type="image/png" href="/favicon.png" />
	{/if}
	{@html data.screenshotHTML}
	{@html preload}
	{#if font_urls.length > 0}
		<link rel="preconnect" href="https://fonts.gstatic.com" />
		<link rel="preconnect" href="https://fonts.googleapis.com" />
		{#each font_urls as font_url}
			{@html `<${""}link
					rel="preload"
					as="style"
					href="${font_url}&display=swap"
					onload="this.onload=null;this.rel='stylesheet'" /${""}>`}
		{/each}
	{/if}
	{@html `<${""}style>
	${hiddenFieldsCSS}
	#formcrafts-form {
		${coverStyle}
	}
	#form {
		${formStyle}
	}
	#form-cover {
		${formCoverStyle}
	}		
	@media only screen and (max-width: 768px) {
		body, html {
			height: 100%;
		}
		body {
			display: flex;
		}
		#formcrafts-form { 
			${formcraftsForm}
		}
		.form-header {
			display: none;
		}
	}	
	@media only screen and (min-width: 768px) {
		#form-cover { 
			${formStyleDesktop}
		}
		#formcrafts-form { 
			${coverStyleDesktop}
			min-height: 100svh;
			${formcraftsForm}
		}
		.form-header {
			display: flex;
		}
	}
	</${""}style>`}
	<title>{data.formEmbed.name}{test ? " - Test" : ""}</title>
	<link rel="canonical" href="{PUBLIC_APP_URL}/{data.formEmbed.key}" />
	<meta name="application-name" content="{data.formEmbed.name}" />
	<meta
		name="theme-color"
		content="{data.formEmbed.styling.baseStyling.accentColor}" />
	<meta name="robots" content="noindex, indexifembedded, nofollow" />
</svelte:head>
<ska:html id="form-page-html" lang="{data.formLanguage.value}"></ska:html>
<svelte:body />

{#if iframe}
	{@html `
	<script type="module">
		if (typeof window !== "undefined") {
			const form = document.getElementById("form-cover");
			const formStyles = getComputedStyle(form);
			const height = parseFloat(formStyles.height.replace("px", ""));
			if (!isNaN(height)) {	
				window.parent.postMessage({
					type: "height",
					content: height
				},"*");				
			}
		}
	</script>
`}
{/if}
{#if iframe === false}
	<div class="top-left-box">
		{#if header && (header.embed || header.label || header.link)}
			<div class="form-header">
				{#if header.embed}
					{@const aspect =
						header.embed.width && header.embed.height
							? (header.embed.width / header.embed.height).toFixed(4)
							: "auto"}
					<svelte:element
						this="{header.link ? 'a' : 'div'}"
						{...header.link
							? { href: getLink(header.link), target: "_blank" }
							: {}}
						style="height: {header.label
							? '1.7rem'
							: '1.9rem'}; max-width: 130px"
						class="form-header-embed">
						{#if header.embed.type === "emoji"}
							<span style="font-size: 1.6rem">
								{@html header.embed.content}
							</span>
						{:else if header.embed.type === "svg"}
							{#key header.embed.content}
								<svg-load
									height="100%"
									width="100%"
									max-width="100%"
									max-height="100%"
									style="display: flex; height: 100%; aspect-ratio: {aspect}"
									src="{getImageSrc(header.embed)}"></svg-load>
							{/key}
						{:else if header.embed.type === "image"}
							<img
								style="height: 100%; width: auto"
								width="{header.embed.width ?? 'auto'}"
								height="{header.embed.height ?? 'auto'}"
								src="{getImageSrc(header.embed)}"
								alt="{header.label} logo" />
						{/if}
					</svelte:element>
				{/if}
				{#if header.label && header.link}
					<a
						href="{getLink(header.link)}"
						target="_blank"
						class="form-header-label">
						{header.label}
					</a>
				{:else if header.label}
					<span class="form-header-label">{header.label}</span>
				{/if}
			</div>
		{/if}
		<div class="notification-container">
			{#if data.formLanguages.length > 1 && (data.formEmbed.config.showLanguageSwitcher || test)}
				<div class="notification-box" style="padding: 0 0.5rem">
					{#each data.formLanguages as language}
						<a
							data-sveltekit-reload
							class="notification-link"
							class:active="{language.value === data.formLanguage.value}"
							title="View in {language.label}"
							href="{getLanguageLink(language.value)}">
							<span>{language.value.toUpperCase()}</span>
						</a>
					{/each}
				</div>
			{/if}
			{#if test}
				<div class="notification-box">Test</div>
				{#if data.formEmbed.config.privacyMode}
					<a
						href="{import.meta.env.PUBLIC_SITE_URL}/help/features/privacy-mode"
						target="_blank"
						class="notification-box">
						Privacy mode
					</a>
				{/if}
				{#if stripe_field}
					{#if stripe_field.settings.test === true}
						<div class="notification-box">Stripe test mode</div>
					{:else}
						<div class="notification-box">Stripe live mode</div>
					{/if}
				{/if}
				{#each integration_errors as error}
					<div class="notification-box type-error">
						{error}
					</div>
				{/each}
				{#each $debug as item}
					<div class="notification-box type-error">
						{@html item.message}
					</div>
				{/each}
			{/if}
		</div>
	</div>
{/if}

<div id="formcrafts-form" class="form-container">
	{#if data.formEmbed.fields.length === 0}
		<div
			class="fixed-cover all-center"
			style="padding: 1rem 3rem; text-align: center">
			Your form does not have any fields. Did you forget to publish your
			changes?
		</div>
	{:else}
		<Form {...props}></Form>
	{/if}
</div>

{#if plan.whitelabel === false && iframe === false}
	<a
		use:tooltip="{{
			content: 'The powered by link is automatically removed from paid plans',
			disabled: test === false
		}}"
		href="{powered_by_link}"
		target="_blank"
		class="powered-by-link">
		{@html translate.powered_by.replace(
			"{0}",
			'<span style="font-weight: 600">formcrafts</span>'
		)}
	</a>
{/if}
